/* eslint-disable complexity */

import { useMemo } from 'react';
import acquisitionTypes from '../support/acquisitionTypes';
import acquisitionActions, {
  type AcquisitionAction,
} from '../support/acquisitionActions';
import useSelectedIndex from './stateful/useSelectedIndex';
import { useAcquisitionType } from './stateful/useAcquisitionType';
import useSelectedSubscriptionOption from '../shared/hooks/useSelectedSubscriptionOption';
import useOnLoad from './queries/useOnLoad';

const canSaveWithUltraPacks = (hasPreferredPricing, selectedSize) => {
  if (!hasPreferredPricing && selectedSize) {
    const fivePackPrice = selectedSize.imagePackSavings?.find(
      (pack) => pack.packSize === 5
    );
    if (fivePackPrice?.pricePerDownload < selectedSize.rawPrice) {
      return true;
    }
  }
  return false;
};

const useAvailableAcquisitionActions = () => {
  const { acquisitionType } = useAcquisitionType();
  const { response: { config: { hasPreferredPricing } = {} } = {} } =
    useOnLoad();
  const { selectedOption, selectedSize } = useSelectedIndex();
  const { selectedSubscriptionOption } = useSelectedSubscriptionOption();

  const response = useMemo<Array<AcquisitionAction>>(() => {
    const noSelectedOptionForAcquisitionType =
      acquisitionType === acquisitionTypes.RESTRICTED ||
      acquisitionType === acquisitionTypes.SUBSCRIPTION_LICENSE ||
      acquisitionType === acquisitionTypes.ALACARTE_LICENSE ||
      acquisitionType === acquisitionTypes.BATCH_DOWNLOAD;
    const noSelectedOptionForDownloadCompleted =
      acquisitionType === acquisitionTypes.MODIFIED_DOWNLOAD_COMPLETED;
    const noPreferredPricingForAcquisitionType =
      acquisitionType === acquisitionTypes.BATCH_DOWNLOAD ||
      acquisitionType === acquisitionTypes.RESTRICTED;

    const pricingValid =
      hasPreferredPricing !== undefined || noPreferredPricingForAcquisitionType;
    const acquisitionTypeValid = !!acquisitionType;
    const selectedOptionValid =
      !!selectedOption.type ||
      noSelectedOptionForAcquisitionType ||
      noSelectedOptionForDownloadCompleted;

    // need a value back from useOnLoad hook for hasPreferredPricing before we
    // compute actions, exception for batch download which doesn't use preferred pricing
    const enabled = pricingValid && acquisitionTypeValid && selectedOptionValid;
    if (!enabled) {
      return [];
    }

    const availableTypes: Array<AcquisitionAction> = [];
    switch (acquisitionType) {
      case acquisitionTypes.LICENSE:
        if (selectedSize.license === 'commercial-market-freeze') {
          availableTypes.push(acquisitionActions.CONTACT_US_MARKET_FREEZE);
        } else if (selectedSize.license === 'ultrapack') {
          availableTypes.push(acquisitionActions.BUY_ULTRA_PACK);
        } else if (
          !selectedSize.name &&
          selectedOption?.type === 'customlicense'
        ) {
          availableTypes.push(
            acquisitionActions.SELECT_OPTIONS,
            acquisitionActions.SAVE_TO_CART
          );
        } else {
          availableTypes.push(acquisitionActions.CART);
          if (canSaveWithUltraPacks(hasPreferredPricing, selectedSize)) {
            availableTypes.push(acquisitionActions.VIEW_ULTRA_PACK_UPSELL);
          }
        }
        break;
      case acquisitionTypes.MODIFIED_LICENSE:
        availableTypes.push(acquisitionActions.CART);
        break;
      case acquisitionTypes.ULTRA_PACK_UPSELL:
        availableTypes.push(
          acquisitionActions.BUY_ULTRA_PACK,
          acquisitionActions.VIEW_PRICING_OPTIONS
        );
        break;
      case acquisitionTypes.LIMITED_USE_PRODUCTS:
        availableTypes.push(
          acquisitionActions.CART,
          acquisitionActions.RETURN_TO_ROYALTY_FREE
        );
        break;
      case acquisitionTypes.CART_ADP_CONFIRMATION:
        if (selectedOption?.type === 'customlicense') {
          availableTypes.push(
            acquisitionActions.SELECT_OPTIONS,
            acquisitionActions.VIEW_CART
          );
        } else {
          availableTypes.push(
            acquisitionActions.PROCEED_TO_CHECKOUT,
            acquisitionActions.VIEW_CART
          );
          if (canSaveWithUltraPacks(hasPreferredPricing, selectedSize)) {
            availableTypes.push(acquisitionActions.GET_WITH_ULTRAPACK);
          }
        }
        break;
      case acquisitionTypes.RESTRICTED:
        availableTypes.push(acquisitionActions.REQUEST_PREVIEW);
        break;
      case acquisitionTypes.DOWNLOAD:
        availableTypes.push(acquisitionActions.DOWNLOAD);
        break;
      case acquisitionTypes.MODIFIED_DOWNLOAD:
        availableTypes.push(acquisitionActions.MODIFIED_DOWNLOAD);
        break;
      case acquisitionTypes.MODIFIED_CART_CONFIRMATION:
        availableTypes.push(acquisitionActions.PROCEED_TO_CHECKOUT);
        availableTypes.push(acquisitionActions.CART);
        break;
      case acquisitionTypes.MODIFIED_DOWNLOAD_COMPLETED:
        availableTypes.push(acquisitionActions.MODIFIED_DOWNLOAD_COMPLETED);
        break;
      case acquisitionTypes.BATCH_DOWNLOAD:
        availableTypes.push(acquisitionActions.BATCH_DOWNLOAD);
        break;
      case acquisitionTypes.MODIFIED_LICENSE_REDOWNLOAD:
        availableTypes.push(acquisitionActions.MODIFIED_LICENSE_REDOWNLOAD);
        break;
      case acquisitionTypes.MODIFIED_LIMITED_USE_PRODUCTS:
        availableTypes.push(
          acquisitionActions.CART,
          acquisitionActions.RETURN_TO_MODIFIED_ROYALTY_FREE
        );
        break;
      case acquisitionTypes.SUBSCRIPTION_DOWNLOAD:
        availableTypes.push(acquisitionActions.SUBSCRIPTION_DOWNLOAD);
        break;
      case acquisitionTypes.SUBSCRIPTION_LICENSE:
        if (!selectedSize.name && selectedOption?.type === 'customlicense') {
          availableTypes.push(
            acquisitionActions.SELECT_OPTIONS,
            acquisitionActions.SAVE_TO_CART
          );
        } else if (selectedSubscriptionOption === 'ultrapack') {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_BUY_NOW);
        } else if (selectedSubscriptionOption === 'single') {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_BUY_NOW);
          availableTypes.push(acquisitionActions.SUBSCRIPTION_ADD_TO_CART);
        } else {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_SUBSCRIBE);
        }
        if (selectedOption?.type !== 'customlicense') {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_LINKS);
        }
        break;
      case acquisitionTypes.ALACARTE_LICENSE:
        if (!selectedSize.name && selectedOption?.type === 'customlicense') {
          availableTypes.push(
            acquisitionActions.SELECT_OPTIONS,
            acquisitionActions.SAVE_TO_CART
          );
        } else if (selectedSubscriptionOption === 'ultrapack') {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_BUY_NOW);
        } else {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_BUY_NOW);
          availableTypes.push(acquisitionActions.SUBSCRIPTION_ADD_TO_CART);
        }
        if (selectedOption?.type !== 'customlicense') {
          availableTypes.push(acquisitionActions.SUBSCRIPTION_LINKS);
        }
        break;
      default:
        break;
    }
    return availableTypes;
  }, [
    acquisitionType,
    selectedSize,
    selectedOption,
    selectedSubscriptionOption,
    hasPreferredPricing,
  ]);

  return { response };
};

export default useAvailableAcquisitionActions;
